import type { CSSProperties } from 'react';

export interface SymbolProps {
  /** Material Symbols ligature name, e.g. `shopping_bag`, `bar_chart`. */
  name: string;
  /** Rendered size in px. Also drives the font's optical-size axis. */
  size?: number;
  /** Solid (FILL 1) vs outlined (FILL 0). Use fill for the active nav item. */
  fill?: boolean;
  /** Stroke weight, 100–700. Defaults to 500 to match our 600 UI type. */
  weight?: number;
  /** Emphasis nudge, -50–200. A small positive grade keeps light glyphs on
   *  dark surfaces from thinning out. */
  grade?: number;
  className?: string;
  /**
   * Accessible name. Omit for icons that merely decorate adjacent text — the
   * glyph is then hidden from assistive tech instead of being read as its
   * ligature (a bare `<span>shopping_bag</span>` is announced letter salad).
   */
  label?: string;
}

/**
 * Material Symbols Rounded icon — the admin console's counterpart to the
 * Flutter apps' `UtsaahaIcon`, so both surfaces draw from one icon set.
 *
 * Material Symbols is a variable font, which is why this takes axes rather
 * than importing two icon files: the same glyph covers rest and active states
 * by animating FILL, and `weight` lets a 16px glyph carry enough stroke to
 * read beside 14px semibold type.
 *
 * The font is loaded once in `index.css`; `font-display: block` there prevents
 * the flash of raw ligature text before it arrives.
 */
export function Symbol({
  name,
  size = 20,
  fill = false,
  weight = 500,
  grade = 0,
  className = '',
  label,
}: SymbolProps) {
  const style: CSSProperties = {
    fontSize: `${size}px`,
    lineHeight: 1,
    width: `${size}px`,
    height: `${size}px`,
    fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${
      size <= 20 ? 20 : size >= 40 ? 40 : 24
    }`,
  };

  return (
    <span
      className={`material-symbols-rounded select-none ${className}`}
      style={style}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      {name}
    </span>
  );
}
